import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { FormButton, SpinnerOverlay } from "components";
import {
  FormikSelectInput,
  FormikTextInput,
} from "components/UI/Inputs/FormikInputs";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { LARGO, mensajeLargo } from "consts/limites-campos";
import { TIPOS_FERIADO } from "consts/tipos-feriado";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import styles from "styles/pages/ModalActions.module.scss";
import { RNHFeriadoUpd } from "services/services-mia/programa-salud/feriados/RNHFeriadoUpd";
import { RNHFeriadoGet } from "services/services-mia/programa-salud/feriados/RNHFeriadoGet";

export default function UpdateModal({ row, closeModal, updateList }) {
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
  const [data, setData] = useState();
  const [inRequest, setInRequest] = useState(false);
  const formikRef = useRef();

  // Se relee el registro aunque la fila ya traiga nombre y tipo: lo que importa es la
  // VERSION. Si alguien mas lo modifico desde que se cargo la grilla, la de la fila
  // quedo vieja y el Upd lo rechaza.
  const getFeriadoData = async () => {
    setInRequest(true);
    const { data: result } = await RNHFeriadoGet(credentials, { id: row.id });
    const encontrado = Array.isArray(result)
      ? result.find((f) => String(f.rnhFeriadoId) === String(row.id))
      : null;
    setInRequest(false);

    if (!encontrado) {
      setSnackBarInfo({
        message: "No se pudo cargar el feriado seleccionado.",
        severity: "error",
        open: true,
      });
      closeModal();
      return;
    }

    formikRef.current.setValues({
      nombre: encontrado.rnhFeriadoNombre ?? "",
      tipo: encontrado.rnhFeriadoTipo ?? "",
    }, false);
    setData(encontrado);
  };

  const guardarFeriado = async (values) => {
    setInRequest(true);
    const response = await RNHFeriadoUpd(credentials, {
      // Lo recien leido manda; la fila de la grilla es el respaldo por si el Get fallo.
      id: data?.rnhFeriadoId ?? row.id,
      nombre: values.nombre,
      tipo: values.tipo,
      version: data?.rnhFeriadoVersion ?? row.version,
    });
    setInRequest(false);

    if (!response.ok) return;

    setSnackBarInfo({
      message: "Feriado modificado correctamente",
      severity: "success",
      open: true,
    });
    formikRef.current.resetForm();
    updateList();
    closeModal();
  };

  useEffect(() => {
    getFeriadoData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        nombre: row?.nombre ?? "",
        // `tipoCod` y no `tipo`: la fila guarda la etiqueta en `tipo` para la columna,
        // y el select necesita el codigo crudo para preseleccionarse.
        tipo: row?.tipoCod ?? "",
      }}
      validationSchema={yup.object({
        nombre: yup
          .string()
          .max(LARGO.nombre, mensajeLargo(LARGO.nombre))
          .required("Debe ingresar un nombre"),
        tipo: yup.string().required("Debe elegir un tipo"),
      })}
      onSubmit={(values) => {
        guardarFeriado(values);
      }}
    >
      <Form className={styles.inputs} noValidate>
        <div className={styles["column"]}>
          <Grid
            container
            rowSpacing={0}
            columnSpacing={2}
            sx={{
              width: "100%",
              "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
            }}
          >
            <Grid item xs={8}>
              <FormikTextInput
                fullWidth={true}
                name="nombre"
                labelText="Nombre"
                inputProps={{ maxLength: LARGO.nombre }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormikSelectInput name="tipo" labelText="Tipo">
                <option value="" disabled>
                  Seleccione
                </option>
                {TIPOS_FERIADO.map(({ cod, label }) => (
                  <option key={cod} value={cod}>
                    {label}
                  </option>
                ))}
              </FormikSelectInput>
            </Grid>
          </Grid>
          <FormButton
            inRequest={inRequest}
            newFormData={{ button: "Modificar" }}
            onCloseModal={closeModal}
          />
          {inRequest && <SpinnerOverlay />}
        </div>
      </Form>
    </Formik>
  );
}
